import { useEffect, useState } from "react";
import { api, type ModeStatusResponse } from "../../api/client";
import { cn } from "@/lib/utils";
import { type Tone, toneBadge } from "@/lib/semantics";

const CHIP =
  "inline-flex w-fit items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap";

/**
 * S56c — read-only environment indicator, bound to GET /api/mode/status.
 *
 * <p>DEMO and LIVE are separate dedicated deployments; this chip simply reports
 * which environment this deployment is — it is NOT a switch (there is no runtime
 * mode switch). LIVE renders red (`badge--live`) per the frontend LIVE-banner
 * convention; DEMO blue; REPLAY neutral. Mounted in the shared NavBar (every
 * AppShell page). A failed/missing descriptor degrades to an explicit
 * placeholder rather than breaking the page it sits in.
 *
 * @param compact drops the "Environment: " prefix (the chip sits next to the DAX Bot
 *   wordmark in the header, where the label is redundant) — value + account only.
 */
export default function EnvironmentChip({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<ModeStatusResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .modeStatus()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Defensive: a missing/partial descriptor must degrade to an explicit
  // placeholder — never crash the page it sits in (partial-un-stubbing
  // discipline). §25: a failed fetch says "?" (reason in the title), distinct
  // from the initial-load "…".
  if (failed || (data != null && !data.environmentType)) {
    return (
      <span
        className={cn(CHIP, toneBadge("idle"))}
        data-testid="environment-chip"
        data-environment="UNKNOWN"
        title="GET /api/mode/status en échec ou sans environnement"
      >
        {compact ? "Env ?" : "Environnement : ?"}
      </span>
    );
  }
  if (!data) {
    return (
      <span
        className={cn(CHIP, toneBadge("idle"))}
        data-testid="environment-chip"
        data-environment="UNKNOWN"
      >
        {compact ? "Env …" : "Environnement : …"}
      </span>
    );
  }

  const tone: Tone =
    data.environmentType === "LIVE"
      ? "live"
      : data.environmentType === "DEMO"
        ? "info"
        : "idle";
  const account = data.accountId ? ` (${data.accountId})` : "";

  return (
    <span
      className={cn(CHIP, toneBadge(tone))}
      data-testid="environment-chip"
      data-environment={data.environmentType}
      title={data.banner?.text}
    >
      {compact
        ? `${data.environmentType}${account}`
        : `Environment: ${data.environmentType}${account}`}
    </span>
  );
}
